import { useCallback } from "react";

import { useJitsu } from "@jitsu/react";

import { JitsuEvent } from "@/data/events/JitsuEvent";
import { useQueueVideoResponse } from "@/features/socratic/hooks/use-queue-video-response";
import {
  AnswerBlockFragment,
  SocraticDialogueFragment,
} from "@/generated/graphql";

interface Props {
  answerBlock: AnswerBlockFragment;
}

/**
 * Hook returning a callback that writes a chosen completed Socratic dialogue
 * to the answer block snapshot. Used when the student explicitly switches
 * which attempt is submitted from the list of completed attempts.
 *
 * Reuses `useQueueVideoResponse` so the snapshot shape (and the snapshot
 * queue lifecycle) stays identical to the auto-write path that runs on
 * recording completion.
 */
export function useSelectVideoResponseAttempt({ answerBlock }: Props) {
  const queueVideoResponse = useQueueVideoResponse({ answerBlock });
  const { track } = useJitsu();

  return useCallback(
    (dialogue: SocraticDialogueFragment) => {
      if (dialogue.turns[0]?.response == null) return;
      queueVideoResponse({ dialogueId: dialogue.id });

      track(JitsuEvent.VIDEO_RESPONSE_ATTEMPT_SELECTED, {
        dialogue_id: dialogue.id,
        attempt_number: dialogue.attemptNumber,
      });
    },
    [queueVideoResponse, track]
  );
}
